import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { fmtNum } from "../lib/format";

/**
 * Advisor-only dialog to manually override the average cost of an open holding.
 * When active, the override replaces the auto-computed avg cost from the
 * transaction ledger (invested value & unrealized P&L recompute accordingly).
 * "Reset to auto" clears the override.
 */
export default function EditAvgCostDialog({ clientId, holding, open, onOpenChange, onSaved }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !holding) return;
    setValue(String(holding.avg_cost ?? ""));
  }, [open, holding]);

  if (!holding) return null;

  const submit = async () => {
    const num = Number(value);
    if (!num || num <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    setSaving(true);
    try {
      await apiClient.put(
        `/advisor/clients/${clientId}/holdings/${encodeURIComponent(holding.symbol)}/avg-cost`,
        { avg_cost: num }
      );
      toast.success(`Avg cost updated for ${holding.symbol}`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await apiClient.delete(
        `/advisor/clients/${clientId}/holdings/${encodeURIComponent(holding.symbol)}/avg-cost`
      );
      toast.success(`Reset ${holding.symbol} to auto avg cost`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none" data-testid="edit-avg-cost-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Edit average cost</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Overrides the auto-calculated avg cost for {holding.symbol}. Invested value
            and unrealized P&amp;L will use this figure until reset.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
            <div>
              <div className="text-muted-foreground uppercase tracking-[0.18em] text-[10px]">Symbol</div>
              <div className="text-foreground font-semibold text-sm">{holding.symbol}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-[0.18em] text-[10px]">Qty</div>
              <div className="text-foreground font-semibold text-sm tabular">{fmtNum(holding.quantity, 2)}</div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-[0.18em] text-[10px]">Auto avg</div>
              <div className="text-foreground font-semibold text-sm tabular">
                {fmtNum(holding.computed_avg_cost ?? holding.avg_cost)}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground uppercase tracking-[0.18em] text-[10px]">Current LTP</div>
              <div className="text-foreground font-semibold text-sm tabular">
                {holding.ltp != null ? fmtNum(holding.ltp) : "—"}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              New average cost (₹)
            </label>
            <input
              data-testid="avg-cost-input"
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring tabular"
            />
            {holding.avg_cost_override ? (
              <p className="text-[10px] text-lime mt-1 font-mono uppercase tracking-[0.18em]">
                Override active
              </p>
            ) : null}
          </div>
        </div>

        <DialogFooter className="mt-4 flex-row justify-between sm:justify-between">
          <button
            type="button"
            onClick={reset}
            disabled={saving || !holding.avg_cost_override}
            data-testid="avg-cost-reset"
            className="border border-border hover:bg-secondary px-3 py-2 text-xs font-medium uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <RotateCcw className="h-3 w-3" strokeWidth={2} />
            Reset to auto
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="border border-border hover:bg-secondary px-4 py-2 text-xs font-medium uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="avg-cost-save"
              className="bg-lime hover:brightness-110 text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
